import React, { useEffect, useState } from "react";
import { Download, X, Share2, Plus, ChevronDown, MoreHorizontal } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useInstall } from "@/context/InstallContext";

/* ============================================================================
   SmartInstallBanner (ORIO Warm / Trade Republic style)
   ----------------------------------------------------------------------------
   - Android/Edge Chromium: cattura `beforeinstallprompt` → 1-tap OS-native install
   - iOS Safari: modale con guida visiva (Safari frame animato)
   - Android non-Chrome (Firefox / Samsung): modale con guida "menu ⋮ → Installa app"
   - App già installata (standalone): non appare mai
   - Cooldown 7 giorni dopo dismiss, delay 18s di engagement prima di apparire
   ========================================================================== */

const ENGAGEMENT_DELAY_MS = 18_000;
const COOLDOWN_DAYS = 7;
const STORAGE_KEY = "orio_install_banner_dismissed_until";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent || "") && !window.MSStream;
const isAndroid = () => /Android/i.test(navigator.userAgent || "");
const detectPlatform = () => (isAndroid() ? "android" : isIOS() ? "ios" : "desktop");

const isInCooldown = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v ? Date.now() < Number(v) : false;
  } catch { return false; }
};
const setCooldown = () => {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now() + COOLDOWN_DAYS * 24 * 60 * 60 * 1000));
  } catch { /* ignore */ }
};

const trackEvent = (event, standalone = false) => {
  try {
    api.post("/install-analytics/event", {
      event, platform: detectPlatform(), standalone,
    }).catch(() => {});
  } catch { /* ignore */ }
};

export default function SmartInstallBanner() {
  const { canPrompt, promptInstall, isStandalone, installed } = useInstall();
  const [visible, setVisible] = useState(false);
  const [modal, setModal] = useState(null); // "ios" | "android" | null

  useEffect(() => {
    if (isStandalone || installed) return;
    if (isInCooldown()) return;
    if (!isIOS() && !isAndroid()) return; // solo mobile

    const t = setTimeout(() => { setVisible(true); trackEvent("shown"); }, ENGAGEMENT_DELAY_MS);
    return () => clearTimeout(t);
  }, [isStandalone, installed]);

  const dismiss = () => { setVisible(false); setCooldown(); trackEvent("dismissed"); };

  const install = async () => {
    trackEvent("cta_clicked");
    if (canPrompt) {
      const outcome = await promptInstall();
      if (outcome === "accepted") { setVisible(false); return; }
      // dismissed or unavailable → show manual instructions
    }
    // No native prompt available (or user just dismissed the OS dialog):
    setModal(isIOS() ? "ios" : "android");
    trackEvent("modal_opened");
  };

  if (!visible && !modal) return null;

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ y: 90, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 90, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            data-testid="install-banner"
            className="fixed z-[9000] pointer-events-none"
            style={{
              bottom: "calc(70px + env(safe-area-inset-bottom, 0px))",
              left: "max(16px, env(safe-area-inset-left, 0px))",
              right: "max(16px, env(safe-area-inset-right, 0px))",
            }}
          >
            <div
              className="pointer-events-auto max-w-md mx-auto rounded-3xl overflow-hidden border"
              style={{
                background: "#FFFFFF",
                borderColor: "#F0EBE3",
                boxShadow: "0 24px 48px -18px rgba(0,0,0,0.22), 0 6px 14px -6px rgba(0,0,0,0.08)",
              }}
            >
              <div className="flex items-center gap-3 p-3 pl-4">
                {/* App icon — ORIO logo tinted warm */}
                <div
                  className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0"
                  style={{
                    background: "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)",
                    boxShadow: "0 8px 18px -6px rgba(255,92,42,0.55)",
                  }}
                >
                  <Download className="h-5 w-5 text-white" strokeWidth={2.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-[15px] font-semibold text-[#1A1A1A] leading-tight">
                    Installa ORIO
                  </div>
                  <div className="text-[11.5px] text-[#7A7A7A] mt-0.5 leading-tight">
                    {isIOS() ? "2 tap in Safari · niente App Store" : "Su Google Play · funziona offline"}
                  </div>
                </div>
                <button
                  onClick={install}
                  data-testid="install-banner-cta"
                  className="orio-cta-btn relative px-4 py-2.5 rounded-full text-black text-[12px] font-semibold overflow-hidden shrink-0"
                >
                  <span className="relative z-10">
                    {isIOS() ? "Come?" : "Scarica"}
                  </span>
                </button>
                <button
                  onClick={dismiss}
                  data-testid="install-banner-close"
                  aria-label="Chiudi"
                  className="h-10 w-10 -mr-1 flex items-center justify-center rounded-full text-[#9A9A9A] hover:text-[#1A1A1A] hover:bg-[#F4EFE6] active:bg-[#F0EBE3] transition-colors shrink-0"
                >
                  <X className="h-4.5 w-4.5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {modal === "ios" && (
          <IOSVisualModal onClose={() => { setModal(null); dismiss(); }} />
        )}
        {modal === "android" && (
          <AndroidVisualModal onClose={() => { setModal(null); dismiss(); }} />
        )}
      </AnimatePresence>
    </>
  );
}

/* ============================================================================
   iOS Visual Modal — Trade Republic warm style
   ========================================================================== */
function IOSVisualModal({ onClose }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % 2), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9500] flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-4"
      data-testid="install-ios-modal"
      onClick={onClose}
    >
      <div className="absolute inset-0" style={{ background: "rgba(28,20,10,0.55)", backdropFilter: "blur(8px)" }} />
      <motion.div
        initial={{ y: 60, scale: 0.97 }} animate={{ y: 0, scale: 1 }} exit={{ y: 60, scale: 0.97 }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-sm rounded-3xl overflow-hidden border"
        style={{
          background: "#FBF7F1",
          borderColor: "#F0EBE3",
          boxShadow: "0 40px 80px -20px rgba(0,0,0,0.35)",
        }}
      >
        <button
          onClick={onClose}
          data-testid="install-ios-close"
          aria-label="Chiudi"
          className="absolute top-3 right-3 h-10 w-10 z-20 flex items-center justify-center rounded-full text-[#9A9A9A] hover:text-[#1A1A1A] hover:bg-[#F4EFE6] transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="px-6 pt-7 pb-4">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#9A9A9A] mb-1.5">
            Installazione iPhone
          </div>
          <h3 className="font-display text-[26px] leading-[1.15] tracking-tight text-[#1A1A1A]">
            Apple ti chiede solo{" "}
            <span className="orio-grad-text">2 tap</span>
          </h3>
          <p className="mt-2.5 text-[13.5px] text-[#5A5A5A] leading-relaxed">
            Apri questa pagina in <strong className="text-[#1A1A1A]">Safari</strong>, poi segui l&apos;animazione:
          </p>
        </div>

        {/* Animated Safari mock */}
        <div className="px-6">
          <SafariFrameLight highlight={step} />
        </div>

        {/* Steps */}
        <div className="px-6 pt-5 pb-5 space-y-2.5">
          <StepRow
            n={1} active={step === 0}
            icon={<Share2 className="h-4 w-4" />}
            title="Tocca Condividi"
            sub="Icona con freccia in su, nella barra inferiore di Safari."
          />
          <StepRow
            n={2} active={step === 1}
            icon={<Plus className="h-4 w-4" />}
            title="Aggiungi alla schermata Home"
            sub="Scorri se non lo vedi subito, poi conferma con Aggiungi."
          />
        </div>

        <div className="px-6 pb-6">
          <button
            onClick={onClose}
            data-testid="install-ios-confirm"
            className="w-full orio-cta-btn relative px-5 py-3.5 rounded-full text-black font-semibold overflow-hidden"
          >
            <span className="relative z-10">Ho capito, lo provo</span>
          </button>
          <p className="text-center text-[11px] text-[#9A9A9A] mt-3">
            🔒 ORIO apparirà come vera app sulla tua Home. Funziona anche offline.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ============================================================================
   Android Visual Modal (fallback per browser senza beforeinstallprompt:
   Firefox, Samsung Internet, in-app browsers). Stessa estetica warm.
   ========================================================================== */
function AndroidVisualModal({ onClose }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % 2), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9500] flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-4"
      data-testid="install-android-modal"
      onClick={onClose}
    >
      <div className="absolute inset-0" style={{ background: "rgba(28,20,10,0.55)", backdropFilter: "blur(8px)" }} />
      <motion.div
        initial={{ y: 60, scale: 0.97 }} animate={{ y: 0, scale: 1 }} exit={{ y: 60, scale: 0.97 }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-sm rounded-3xl overflow-hidden border"
        style={{
          background: "#FBF7F1", borderColor: "#F0EBE3",
          boxShadow: "0 40px 80px -20px rgba(0,0,0,0.35)",
        }}
      >
        <button
          onClick={onClose}
          data-testid="install-android-close"
          aria-label="Chiudi"
          className="absolute top-3 right-3 h-10 w-10 z-20 flex items-center justify-center rounded-full text-[#9A9A9A] hover:text-[#1A1A1A] hover:bg-[#F4EFE6] transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="px-6 pt-7 pb-4">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#9A9A9A] mb-1.5">
            Installazione Android
          </div>
          <h3 className="font-display text-[26px] leading-[1.15] tracking-tight text-[#1A1A1A]">
            Bastano <span className="orio-grad-text">2 tap</span> dal menù
          </h3>
          <p className="mt-2.5 text-[13.5px] text-[#5A5A5A] leading-relaxed">
            Tocca i <strong className="text-[#1A1A1A]">tre puntini ⋮</strong> in alto a destra del browser, poi:
          </p>
        </div>

        {/* Chrome/Android mock */}
        <div className="px-6">
          <ChromeFrameLight highlight={step} />
        </div>

        <div className="px-6 pt-5 pb-5 space-y-2.5">
          <StepRow
            n={1} active={step === 0}
            icon={<MoreHorizontal className="h-4 w-4" />}
            title="Apri il menù del browser"
            sub="Icona ⋮ in alto a destra (Chrome, Edge, Samsung, Firefox)."
          />
          <StepRow
            n={2} active={step === 1}
            icon={<Download className="h-4 w-4" />}
            title="Installa app / Aggiungi alla Home"
            sub="La voce cambia leggermente in base al browser, ma il risultato è lo stesso."
          />
        </div>

        <div className="px-6 pb-6">
          <button
            onClick={onClose}
            data-testid="install-android-confirm"
            className="w-full orio-cta-btn relative px-5 py-3.5 rounded-full text-black font-semibold overflow-hidden"
          >
            <span className="relative z-10">Ho capito, lo provo</span>
          </button>
          <p className="text-center text-[11px] text-[#9A9A9A] mt-3">
            🔒 ORIO apparirà come vera app sulla tua Home. Notifiche push incluse.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* Trade Republic style step row — warm, minimal, active-highlighted. */
function StepRow({ n, icon, title, sub, active }) {
  return (
    <div
      className="flex items-start gap-3 rounded-2xl p-3.5 border transition-all"
      style={{
        borderColor: active ? "#FF7A1A55" : "#F0EBE3",
        background: active ? "rgba(255,122,26,0.06)" : "#FFFFFF",
      }}
    >
      <div
        className="h-7 w-7 rounded-full flex items-center justify-center shrink-0 text-[12px] font-bold transition-colors"
        style={{
          background: active ? "linear-gradient(135deg,#FF7A1A,#FF5C2A)" : "#F4EFE6",
          color: active ? "#FFFFFF" : "#7A7A7A",
        }}
      >
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[13.5px] font-semibold text-[#1A1A1A]">
          {icon} {title}
        </div>
        <div className="text-[11.5px] text-[#6B6B6B] mt-0.5 leading-snug">{sub}</div>
      </div>
    </div>
  );
}

/* Stylized Safari browser frame (light warm) */
function SafariFrameLight({ highlight }) {
  return (
    <div
      className="relative rounded-2xl overflow-hidden border"
      style={{ background: "#FFFFFF", borderColor: "#F0EBE3" }}
    >
      {/* URL bar */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b" style={{ borderColor: "#F0EBE3", background: "#FAF7F0" }}>
        <span className="h-2 w-2 rounded-full" style={{ background: "#E5DED1" }} />
        <div
          className="flex-1 h-5 rounded-md flex items-center justify-center text-[10px] font-mono"
          style={{ background: "#F0EBE3", color: "#9A9A9A" }}
        >
          orio.app
        </div>
        <span className="h-2 w-2 rounded-full" style={{ background: "#E5DED1" }} />
      </div>
      {/* Page area */}
      <div className="h-28 relative flex items-center justify-center" style={{ background: "#FBF7F1" }}>
        <div className="orio-logo-grad h-9 w-9 opacity-50" />
        <AnimatePresence mode="wait">
          {highlight === 1 && (
            <motion.div
              key="add-popup"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              className="absolute left-1/2 -translate-x-1/2 bottom-2 rounded-xl p-2.5 flex items-center gap-2"
              style={{
                width: "82%",
                background: "#FFFFFF",
                border: "1px solid #F0EBE3",
                boxShadow: "0 10px 24px -8px rgba(0,0,0,0.18)",
              }}
            >
              <span className="text-[11px] font-semibold text-[#1A1A1A] flex-1">Aggiungi alla schermata Home</span>
              <Plus className="h-4 w-4" style={{ color: "#FF7A1A" }} strokeWidth={2.6} />
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 1.2, repeat: Infinity }}
                className="absolute -top-9 right-3"
              >
                <ChevronDown className="h-7 w-7" style={{ color: "#FF7A1A" }} strokeWidth={3} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* Safari toolbar */}
      <div
        className="relative flex items-center justify-around px-4 py-2.5 border-t"
        style={{ borderColor: "#F0EBE3", background: "#FAF7F0" }}
      >
        <span className="h-3 w-3 rounded-sm" style={{ background: "#E5DED1" }} />
        <span className="h-3 w-3 rounded-sm" style={{ background: "#E5DED1" }} />
        <div className="relative">
          <motion.div
            animate={highlight === 0 ? { scale: [1, 1.15, 1] } : { scale: 1 }}
            transition={{ duration: 1.2, repeat: highlight === 0 ? Infinity : 0 }}
            className="h-7 w-7 rounded-md flex items-center justify-center"
            style={{
              background: highlight === 0 ? "rgba(255,122,26,0.18)" : "#F0EBE3",
              boxShadow: highlight === 0 ? "0 0 0 2px #FF7A1A" : "none",
            }}
          >
            <Share2 className="h-3.5 w-3.5" style={{ color: highlight === 0 ? "#FF7A1A" : "#7A7A7A" }} strokeWidth={2.6} />
          </motion.div>
          {highlight === 0 && (
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 1.2, repeat: Infinity }}
              className="absolute -top-9 left-1/2 -translate-x-1/2"
            >
              <ChevronDown className="h-7 w-7" style={{ color: "#FF7A1A" }} strokeWidth={3} />
            </motion.div>
          )}
        </div>
        <span className="h-3 w-3 rounded-sm" style={{ background: "#E5DED1" }} />
        <span className="h-3 w-3 rounded-sm" style={{ background: "#E5DED1" }} />
      </div>
    </div>
  );
}

/* Stylized Chrome/Android browser frame (light warm) */
function ChromeFrameLight({ highlight }) {
  return (
    <div
      className="relative rounded-2xl overflow-hidden border"
      style={{ background: "#FFFFFF", borderColor: "#F0EBE3" }}
    >
      {/* Chrome address bar */}
      <div
        className="flex items-center gap-2 px-3 py-2 border-b"
        style={{ borderColor: "#F0EBE3", background: "#FAF7F0" }}
      >
        <div
          className="flex-1 h-5 rounded-full flex items-center justify-center text-[10px] font-mono"
          style={{ background: "#F0EBE3", color: "#9A9A9A" }}
        >
          orio.app
        </div>
        <div className="relative">
          <motion.div
            animate={highlight === 0 ? { scale: [1, 1.15, 1] } : { scale: 1 }}
            transition={{ duration: 1.2, repeat: highlight === 0 ? Infinity : 0 }}
            className="h-6 w-6 rounded-md flex items-center justify-center"
            style={{
              background: highlight === 0 ? "rgba(255,122,26,0.18)" : "transparent",
              boxShadow: highlight === 0 ? "0 0 0 2px #FF7A1A" : "none",
            }}
          >
            <MoreHorizontal className="h-4 w-4" style={{ color: highlight === 0 ? "#FF7A1A" : "#7A7A7A" }} strokeWidth={2.4} />
          </motion.div>
          {highlight === 0 && (
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 1.2, repeat: Infinity }}
              className="absolute -top-8 left-1/2 -translate-x-1/2"
            >
              <ChevronDown className="h-6 w-6" style={{ color: "#FF7A1A", transform: "rotate(180deg)" }} strokeWidth={3} />
            </motion.div>
          )}
        </div>
      </div>
      {/* Page area with dropdown menu at step 1 */}
      <div className="h-32 relative flex items-center justify-center" style={{ background: "#FBF7F1" }}>
        <div className="orio-logo-grad h-10 w-10 opacity-50" />
        <AnimatePresence mode="wait">
          {highlight === 1 && (
            <motion.div
              key="menu-popup"
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="absolute right-2 top-2 rounded-xl p-1.5 flex flex-col gap-0.5"
              style={{
                width: "72%",
                background: "#FFFFFF",
                border: "1px solid #F0EBE3",
                boxShadow: "0 10px 24px -8px rgba(0,0,0,0.18)",
              }}
            >
              <div className="text-[10px] px-2 py-1.5 text-[#9A9A9A]">Nuova scheda</div>
              <div className="text-[10px] px-2 py-1.5 text-[#9A9A9A]">Cronologia</div>
              <div
                className="text-[11px] font-semibold px-2 py-2 rounded-lg flex items-center gap-1.5"
                style={{ background: "rgba(255,122,26,0.14)", color: "#FF7A1A" }}
              >
                <Download className="h-3.5 w-3.5" strokeWidth={2.6} />
                Installa app
              </div>
              <div className="text-[10px] px-2 py-1.5 text-[#9A9A9A]">Condividi…</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
